import { getCurrentDubaiDate } from '../../../../functions/agent/date/getCurrentDubaiDate'
import { timeStringInScheduler } from '../../../../functions/agent/date/timeStringInScheduler'
import { toUTCWithOffset } from '../../../../functions/agent/date/toUTCWithOffset'
import { sendNotification } from '../../../../functions/shared/notifications/sendNotification'
import { type ManualViewingFormValues, type ManualViewingStatus } from './manualOperations.types'

const CLOSED_VIEWING_STATUSES: ReadonlySet<ManualViewingStatus> = new Set<ManualViewingStatus>([
    'Completed',
    'Canceled',
    'Canceled by BSO',
    'Canceled by agent',
    'Canceled by other agent',
    'Rejected'
])

export const shouldScheduleManualViewingUserNotifications = (status: ManualViewingStatus): boolean =>
    !CLOSED_VIEWING_STATUSES.has(status)

const getUnitAddress = (unit: ManualViewingFormValues['unit']): string => {
    const reference = unit.unitNumber ?? unit.unitReference
    return unit.headline.trim() !== ''
        ? `${unit.headline}${unit.headline.includes('Unit') || reference.trim() === '' ? '' : ` ${reference}`}`
        : `${unit.propertyName}${reference.trim() === '' ? '' : ` ${reference}`}`
}

const getNotificationDate = (date: Date, fallback: Date): Date =>
    date.getTime() > fallback.getTime() ? date : new Date(Date.now() + 13000)

/**
 * Creates the same user-facing viewing rules as a viewing created by an agent.
 * Completed and cancelled manual records are historical, so they must not create
 * new user tasks or push notifications.
 */
export const createManualViewingUserNotifications = async (
    values: ManualViewingFormValues,
    viewingId: number
): Promise<void> => {
    if (!shouldScheduleManualViewingUserNotifications(values.status)) return

    const viewingTime = new Date(`${values.datetimeLocal}:00+04:00`)
    if (!Number.isFinite(viewingTime.getTime())) throw new Error('Invalid Dubai viewing time')

    const currentDubaiDate = getCurrentDubaiDate()
    const unitAddress = getUnitAddress(values.unit)
    const recipientName = values.recipientName?.trim()
    const recipientNameForMessage = recipientName != null && recipientName !== '' ? recipientName : 'there'
    const viewingText = `Attention, ${recipientNameForMessage}, you are booked a viewing at ${timeStringInScheduler(viewingTime.toISOString())}, at ${unitAddress}. Be sure to show up on time please.`
    const thirtyMinutesBeforeViewing = toUTCWithOffset(viewingTime, 30)
    const oneHourBeforeViewing = toUTCWithOffset(viewingTime, 60)
    const twoHoursBeforeViewing = toUTCWithOffset(viewingTime, 120)

    if (currentDubaiDate.getTime() < thirtyMinutesBeforeViewing.getTime()) {
        await sendNotification(thirtyMinutesBeforeViewing, values.userId, viewingId, 'user-viewing-before', viewingText)
    }
    if (currentDubaiDate.getTime() < oneHourBeforeViewing.getTime()) {
        await sendNotification(oneHourBeforeViewing, values.userId, viewingId, 'user-viewing-before', viewingText)
    }
    if (currentDubaiDate.getTime() < twoHoursBeforeViewing.getTime()) {
        await sendNotification(twoHoursBeforeViewing, values.userId, viewingId, 'user-viewing-before', viewingText)
    }

    const inMomentViewing = toUTCWithOffset(viewingTime, 1)
    if (currentDubaiDate.getTime() < inMomentViewing.getTime()) {
        await sendNotification(inMomentViewing, values.userId, viewingId, 'user-viewing-before', `Attention, ${recipientNameForMessage}, the viewing at ${unitAddress} has started. We wish you a good viewing experience!`)
    }

    const thirtyMinutesAfterViewing = toUTCWithOffset(viewingTime, -30)
    const feedbackDate = getNotificationDate(thirtyMinutesAfterViewing, currentDubaiDate)
    await sendNotification(feedbackDate, values.userId, viewingId, 'user-viewing-feedback', `Hey, ${recipientNameForMessage}, how was the viewing at ${unitAddress}? Please leave feedback.`)
}
